import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import Footer from "../../core/common/Footer";
import useApi from "../../core/customhook/useApi";
import webservice_url from "../../core/webservice/Webservice_url";
import { all_routes } from "../router/all_routes";
import "./BulkDrugReport.css";
import Datatable from "../../core/common/dataTable";

interface BulkDrugReportItem {
  created_at: any;
  package_name: any;
  check_name: any;
  candidate_name: any;
  case_id: any;
  drug_id: any;
  status_name: string;
  status_id: string;
  report_id: string;
  drug_test_category: string;
  total_candidate: number;
}

const ManifestMark = () => (
  <svg
    className="bdr-manifest-mark"
    viewBox="0 0 40 32"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="0" y="14" width="4" height="18" rx="1.5" />
    <rect x="9" y="8" width="4" height="24" rx="1.5" />
    <rect x="18" y="0" width="4" height="32" rx="1.5" />
    <rect x="27" y="10" width="4" height="22" rx="1.5" />
    <rect x="36" y="18" width="4" height="14" rx="1.5" />
  </svg>
);

const SkeletonRows = ({
  columns = 7,
  rows = 6,
}: {
  columns?: number;
  rows?: number;
}) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={`skeleton-${r}`} className="bdr-skeleton-row">
        {Array.from({ length: columns }).map((__, c) => (
          <td key={c}>
            <span className="bdr-skeleton-bar" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const BulkDrugReport = () => {
  const navigate = useNavigate();

  const [reports, setReports] = useState<BulkDrugReportItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const getBulkDrugReports = useApi<any>(webservice_url.AllDrugReports, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const fetchReports = async () => {
    try {
      const res = await getBulkDrugReports.callApi();
      setReports(res?.data ?? []);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      setReports([]);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleAddReport = () => {
    navigate(all_routes.AdddrugReport);
  };

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current ?? 1);
    setPageSize(pagination.pageSize ?? 10);
  };

  const loading = getBulkDrugReports.loading;

  const stats = useMemo(() => {
    const total = reports.length;

    const createdToday = reports.filter((r) =>
      r.created_at ? dayjs(r.created_at).isSame(dayjs(), "day") : false,
    ).length;

    const counts: Record<string, number> = {};

    reports.forEach((r) => {
      const key = r.status_name || " ";
      counts[key] = (counts[key] || 0) + 1;
    });

    const topStatuses = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2);

    return { total, createdToday, topStatuses };
  }, [reports]);

  const columns: ColumnsType<BulkDrugReportItem> = [
    {
      title: "Sr. No.",
      key: "sr_no",
      width: 85,
      render: (_value, _record, index) =>
        (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Drug Id",
      dataIndex: "drug_id",
      key: "drug_id",
      render: (value) => (
        <span className="bdr-id-value" title={String(value ?? "")}>
          {value ?? "—"}
        </span>
      ),
    },
    {
      title: "Case Id",
      dataIndex: "case_id",
      key: "case_id",
      render: (value) => <span className="bdr-case-chip">{value ?? "—"}</span>,
    },
    {
      title: "Package Name",
      dataIndex: "package_name",
      key: "package_name",
      render: (value) => <span className="bdr-text-cell">{value ?? "—"}</span>,
    },
    {
      title: "Candidate Name",
      dataIndex: "candidate_name",
      key: "candidate_name",
      render: (value) => (
        <span className="bdr-text-cell bdr-candidate-cell">{value ?? "—"}</span>
      ),
    },
    {
      title: "Drug Check Name",
      dataIndex: "check_name",
      key: "check_name",
      render: (value) => <span className="bdr-text-cell">{value ?? "—"}</span>,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => (
        <span className="bdr-date-cell">
          {value ? dayjs(value).format("DD-MM-YYYY") : "—"}
        </span>
      ),
    },
  ];

  return (
    <div className="page-wrapper bdr-page">
      <div className="content p-2 p-md-3">
        <div className="bdr-header">
          <div className="bdr-header-title-row">
            <ManifestMark />
            <div>
              <h4 className="bdr-title">Bulk Drug Reports</h4>
              <p className="bdr-subtitle">
                Monitor, manage, and track all your bulk drug reports from one
                centralized workspace.
              </p>
            </div>
          </div>

          <div className="bdr-header-actions">
            <button
              type="button"
              className="bdr-btn bdr-btn-primary"
              onClick={handleAddReport}
            >
              <i className="ti ti-plus"></i>
              <span className="bdr-btn-label">Add Report</span>
            </button>
          </div>
        </div>

        {!loading && reports.length > 0 && (
          <div className="bdr-stats-row">
            <div className="bdr-stat-card bdr-stat-accent">
              <span className="bdr-stat-bar" />
              <div>
                <p className="bdr-stat-label">Total Reports</p>
                <p className="bdr-stat-value">{stats.total}</p>
              </div>
            </div>

            <div className="bdr-stat-card">
              <span className="bdr-stat-bar bdr-stat-bar-neutral" />
              <div>
                <p className="bdr-stat-label">Created Today</p>
                <p className="bdr-stat-value">{stats.createdToday}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bdr-table-card">
          {loading ? (
            <div className="bdr-table-scroll">
              <table className="bdr-table">
                <thead>
                  <tr>
                    <th className="bdr-col-sr">Sr. No.</th>
                    <th>Drug Id</th>
                    <th>Case Id</th>
                    <th>Package Name</th>
                    <th>Candidate Name</th>
                    <th>Drug Check Name</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  <SkeletonRows columns={7} />
                </tbody>
              </table>
            </div>
          ) : reports.length === 0 ? (
            <div className="bdr-empty-cell">
              <div className="bdr-empty-state">
                <i
                  className="ti ti-file-off bdr-empty-icon"
                  aria-hidden="true"
                ></i>
                <h5 className="bdr-empty-title">No Drug Reports Found</h5>
                <p className="bdr-empty-desc">
                  Bulk drug reports you add will appear here, organized and
                  ready to review.
                </p>
                <button
                  type="button"
                  className="bdr-btn bdr-btn-primary"
                  onClick={handleAddReport}
                >
                  <i className="ti ti-plus"></i>
                  <span className="bdr-btn-label">Add Report</span>
                </button>
              </div>
            </div>
          ) : (
            <Datatable
              columns={columns}
              dataSource={reports}
              rowKey="report_id"
              Selection={false}
              fixedHeader
              tableHeight={650}
              pagination={{
                current: currentPage,
                pageSize,
                total: reports.length,
              }}
              handleTableChange={handleTableChange}
              hideSizeChanger={false}
            />
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BulkDrugReport;

// import { useEffect, useMemo, useState } from "react";
// import { useNavigate } from "react-router";
// import { ColumnsType } from "antd/es/table";
// import dayjs from "dayjs";
// import Footer from "../../core/common/Footer";
// import useApi from "../../core/customhook/useApi";
// import webservice_url from "../../core/webservice/Webservice_url";
// import { all_routes } from "../router/all_routes";
// import "./BulkDrugReport.css";
// import Datatable from "../../core/common/dataTable";

// interface BulkDrugReportItem {
//   created_at: any;
//   package_name: any;
//   check_name: any;
//   candidate_name: any;
//   case_id: any;
//   drug_id: any;
//   status_name: string;
//   status_id: string;
//   report_id: string;
//   drug_test_category: string;
//   total_candidate: number;
// }

// const ManifestMark = () => (
//   <svg
//     className="bdr-manifest-mark"
//     viewBox="0 0 40 32"
//     aria-hidden="true"
//     focusable="false"
//   >
//     <rect x="0" y="14" width="4" height="18" rx="1.5" />
//     <rect x="9" y="8" width="4" height="24" rx="1.5" />
//     <rect x="18" y="0" width="4" height="32" rx="1.5" />
//     <rect x="27" y="10" width="4" height="22" rx="1.5" />
//     <rect x="36" y="18" width="4" height="14" rx="1.5" />
//   </svg>
// );

// const SkeletonRows = ({
//   columns = 7,
//   rows = 6,
// }: {
//   columns?: number;
//   rows?: number;
// }) => (
//   <>
//     {Array.from({ length: rows }).map((_, r) => (
//       <tr key={`skeleton-${r}`} className="bdr-skeleton-row">
//         {Array.from({ length: columns }).map((__, c) => (
//           <td key={c}>
//             <span className="bdr-skeleton-bar" />
//           </td>
//         ))}
//       </tr>
//     ))}
//   </>
// );

// const BulkDrugReport = () => {
//   const navigate = useNavigate();
//   const [reports, setReports] = useState<BulkDrugReportItem[]>([]);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [pageSize, setPageSize] = useState(10);
//   const getBulkDrugReports = useApi<any>(webservice_url.AllDrugReports, {
//     method: "GET",
//     autoCall: false,
//     handleGlobalError: false,
//   });
//   const onTableChange = (tablePagination: any) => {
//   setCurrentPage(tablePagination.current);
//   setCurrentSize(tablePagination.pageSize);
//   handleTableChange?.(tablePagination);
// };

//   const fetchReports = async () => {
//     try {
//       const res = await getBulkDrugReports.callApi();
//       setReports(res?.data ?? []);
//     } catch (err) {
//       console.error(err);
//       setReports([]);
//     }
//   };

//   useEffect(() => {
//     fetchReports();
//   }, []);

//   const handleAddReport = () => {
//     navigate(all_routes.AdddrugReport);
//   };

//   const loading = getBulkDrugReports.loading;

//   const stats = useMemo(() => {
//     const total = reports.length;

//     const createdToday = reports.filter((r) =>
//       r.created_at ? dayjs(r.created_at).isSame(dayjs(), "day") : false,
//     ).length;

//     const counts: Record<string, number> = {};

//     reports.forEach((r) => {
//       const key = r.status_name || " ";
//       counts[key] = (counts[key] || 0) + 1;
//     });

//     const topStatuses = Object.entries(counts)
//       .sort((a, b) => b[1] - a[1])
//       .slice(0, 2);

//     return { total, createdToday, topStatuses };
//   }, [reports]);

//   const columns: ColumnsType<BulkDrugReportItem> = [
//     {
//       title: "Sr. No.",
//       key: "sr_no",
//       width: 85,
//       render: (_value, _record, index) =>
//         (currentPage - 1) * pageSize + index + 1,
//     },
//     {
//       title: "Drug Id",
//       dataIndex: "drug_id",
//       key: "drug_id",
//       render: (value) => (
//         <span className="bdr-id-value" title={String(value ?? "")}>
//           {value ?? "—"}
//         </span>
//       ),
//     },
//     {
//       title: "Case Id",
//       dataIndex: "case_id",
//       key: "case_id",
//       render: (value) => <span className="bdr-case-chip">{value ?? "—"}</span>,
//     },
//     {
//       title: "Package Name",
//       dataIndex: "package_name",
//       key: "package_name",
//       render: (value) => <span className="bdr-text-cell">{value ?? "—"}</span>,
//     },
//     {
//       title: "Candidate Name",
//       dataIndex: "candidate_name",
//       key: "candidate_name",
//       render: (value) => (
//         <span className="bdr-text-cell bdr-candidate-cell">{value ?? "—"}</span>
//       ),
//     },
//     {
//       title: "Drug Check Name",
//       dataIndex: "check_name",
//       key: "check_name",
//       render: (value) => <span className="bdr-text-cell">{value ?? "—"}</span>,
//     },
//     {
//       title: "Created At",
//       dataIndex: "created_at",
//       key: "created_at",
//       render: (value) => (
//         <span className="bdr-date-cell">
//           {value ? dayjs(value).format("DD-MM-YYYY") : "—"}
//         </span>
//       ),
//     },
//   ];

//   return (
//     <div className="page-wrapper bdr-page">
//       <div className="content p-2 p-md-3">
//         <div className="bdr-header">
//           <div className="bdr-header-title-row">
//             <ManifestMark />
//             <div>
//               <h4 className="bdr-title">Bulk Drug Reports</h4>
//               <p className="bdr-subtitle">
//                 Monitor, manage, and track all your bulk drug reports from one
//                 centralized workspace.
//               </p>
//             </div>
//           </div>

//           <div className="bdr-header-actions">
//             <button
//               type="button"
//               className="bdr-btn bdr-btn-primary"
//               onClick={handleAddReport}
//             >
//               <i className="ti ti-plus"></i>
//               <span className="bdr-btn-label">Add Report</span>
//             </button>
//           </div>
//         </div>

//         {!loading && reports.length > 0 && (
//           <div className="bdr-stats-row">
//             <div className="bdr-stat-card bdr-stat-accent">
//               <span className="bdr-stat-bar" />
//               <div>
//                 <p className="bdr-stat-label">Total Reports</p>
//                 <p className="bdr-stat-value">{stats.total}</p>
//               </div>
//             </div>

//             <div className="bdr-stat-card">
//               <span className="bdr-stat-bar bdr-stat-bar-neutral" />
//               <div>
//                 <p className="bdr-stat-label">Created Today</p>
//                 <p className="bdr-stat-value">{stats.createdToday}</p>
//               </div>
//             </div>
//           </div>
//         )}

//         <div className="bdr-table-card">
//           {loading ? (
//             <div className="bdr-table-scroll">
//               <table className="bdr-table">
//                 <thead>
//                   <tr>
//                     <th className="bdr-col-sr">Sr. No.</th>
//                     <th>Drug Id</th>
//                     <th>Case Id</th>
//                     <th>Package Name</th>
//                     <th>Candidate Name</th>
//                     <th>Drug Check Name</th>
//                     <th>Created At</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   <SkeletonRows columns={7} />
//                 </tbody>
//               </table>
//             </div>
//           ) : reports.length === 0 ? (
//             <div className="bdr-empty-cell">
//               <div className="bdr-empty-state">
//                 <i
//                   className="ti ti-file-off bdr-empty-icon"
//                   aria-hidden="true"
//                 ></i>
//                 <h5 className="bdr-empty-title">No Drug Reports Found</h5>
//                 <p className="bdr-empty-desc">
//                   Bulk drug reports you add will appear here, organized and
//                   ready to review.
//                 </p>
//                 <button
//                   type="button"
//                   className="bdr-btn bdr-btn-primary"
//                   onClick={handleAddReport}
//                 >
//                   <i className="ti ti-plus"></i>
//                   <span className="bdr-btn-label">Add Report</span>
//                 </button>
//               </div>
//             </div>
//           ) : (
//             <Datatable
//               columns={columns}
//               dataSource={reports}
//               rowKey="key"
//               Selection={false}
//               fixedHeader
//               tableHeight={650}
//             />
//           )}
//         </div>
//       </div>

//       <Footer />
//     </div>
//   );
// };

// export default BulkDrugReport;

// import { useEffect, useMemo, useState } from "react";
// import { useNavigate } from "react-router";
// import Footer from "../../core/common/Footer";
// import useApi from "../../core/customhook/useApi";
// import webservice_url from "../../core/webservice/Webservice_url";
// import { all_routes } from "../router/all_routes";
// import dayjs from "dayjs";
// import "./BulkDrugReport.css";

// interface BulkDrugReportItem {
//   created_at: any;
//   package_name: any;
//   check_name: any;
//   candidate_name: any;
//   case_id: any;
//   drug_id: any;
//   status_name: string;
//   status_id: string;
//   report_id: string;
//   drug_test_category: string;
//   total_candidate: number;
// }

// const ManifestMark = () => (
//   <svg
//     className="bdr-manifest-mark"
//     viewBox="0 0 40 32"
//     aria-hidden="true"
//     focusable="false"
//   >
//     <rect x="0" y="14" width="4" height="18" rx="1.5" />
//     <rect x="9" y="8" width="4" height="24" rx="1.5" />
//     <rect x="18" y="0" width="4" height="32" rx="1.5" />
//     <rect x="27" y="10" width="4" height="22" rx="1.5" />
//     <rect x="36" y="18" width="4" height="14" rx="1.5" />
//   </svg>
// );

// const SkeletonRows = ({ columns = 7, rows = 6 }: { columns?: number; rows?: number }) => (
//   <>
//     {Array.from({ length: rows }).map((_, r) => (
//       <tr key={`skeleton-${r}`} className="bdr-skeleton-row">
//         {Array.from({ length: columns }).map((__, c) => (
//           <td key={c}>
//             <span className="bdr-skeleton-bar" />
//           </td>
//         ))}
//       </tr>
//     ))}
//   </>
// );

// const BulkDrugReport = () => {
//   const navigate = useNavigate();
//   const [reports, setReports] = useState<BulkDrugReportItem[]>([]);

//   const getBulkDrugReports = useApi<any>(webservice_url.AllDrugReports, {
//     method: "GET",
//     autoCall: false,
//     handleGlobalError: false,
//   });

//   const fetchReports = async () => {
//     try {
//       const res = await getBulkDrugReports.callApi();
//       setReports(res?.data ?? []);
//     } catch (err) {
//       console.error(err);
//       setReports([]);
//     }
//   };

//   useEffect(() => {
//     fetchReports();
//   }, []);

//   const handleAddReport = () => {
//     navigate(all_routes.AdddrugReport);
//   };

//   const loading = getBulkDrugReports.loading;

//   const stats = useMemo(() => {
//     const total = reports.length;
//     const createdToday = reports.filter((r) =>
//       r.created_at ? dayjs(r.created_at).isSame(dayjs(), "day") : false
//     ).length;

//     const counts: Record<string, number> = {};
//     reports.forEach((r) => {
//       const key = r.status_name ||" ";
//       counts[key] = (counts[key] || 0) + 1;
//     });
//     const topStatuses = Object.entries(counts)
//       .sort((a, b) => b[1] - a[1])
//       .slice(0, 2);

//     return { total, createdToday, topStatuses };
//   }, [reports]);

//   const columnCount = 7;

//   return (
//     <div className="page-wrapper bdr-page">
//       <div className="content p-2 p-md-3">
//         {/* ---------------- Page header ---------------- */}
//         <div className="bdr-header">
//           <div className="bdr-header-title-row">
//             <ManifestMark />
//             <div>
//               <h4 className="bdr-title">Bulk Drug Reports</h4>
//               <p className="bdr-subtitle">
//                 Monitor, manage, and track all your bulk drug reports from one
//                 centralized workspace.
//               </p>
//             </div>
//           </div>

//           <div className="bdr-header-actions">
//             <button
//               type="button"
//               className="bdr-btn bdr-btn-primary"
//               onClick={handleAddReport}
//             >
//               <i className="ti ti-plus"></i>
//               <span className="bdr-btn-label">Add Report</span>
//             </button>
//           </div>
//         </div>

//         {!loading && reports.length > 0 && (
//           <div className="bdr-stats-row">
//             <div className="bdr-stat-card bdr-stat-accent">
//               <span className="bdr-stat-bar" />
//               <div>
//                 <p className="bdr-stat-label">Total Reports</p>
//                 <p className="bdr-stat-value">{stats.total}</p>
//               </div>
//             </div>

//             <div className="bdr-stat-card">
//               <span className="bdr-stat-bar bdr-stat-bar-neutral" />
//               <div>
//                 <p className="bdr-stat-label">Created Today</p>
//                 <p className="bdr-stat-value">{stats.createdToday}</p>
//               </div>
//             </div>
//           </div>
//         )}

//         <div className="bdr-table-card">
//           <div className="bdr-table-scroll">
//             <table className="bdr-table">
//               <thead>
//                 <tr>
//                   <th className="bdr-col-sr">Sr. No.</th>
//                   <th>Drug Id</th>
//                   <th>Case Id</th>
//                   <th>Package Name</th>
//                   <th>Candidate Name</th>
//                   <th>Drug Check Name</th>
//                   <th>Created At</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {loading ? (
//                   <SkeletonRows columns={columnCount} />
//                 ) : reports.length > 0 ? (
//                   reports.map((report, index) => (
//                     <tr key={report.report_id} className="bdr-row">
//                       <td className="bdr-col-sr">{index + 1}</td>

//                       <td>
//                         <span
//                           className="bdr-id-value"
//                           title={String(report.drug_id ?? "")}
//                         >
//                           {report.drug_id}
//                         </span>
//                       </td>

//                       <td>
//                         <span className="bdr-case-chip">{report.case_id}</span>
//                       </td>

//                       <td className="bdr-text-cell">{report.package_name}</td>
//                       <td className="bdr-text-cell bdr-candidate-cell">
//                         {report.candidate_name}
//                       </td>
//                       <td className="bdr-text-cell">{report.check_name}</td>

//                       <td className="bdr-date-cell">
//                         {report.created_at
//                           ? dayjs(report.created_at).format("DD MMM YYYY")
//                           : "—"}
//                       </td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td colSpan={columnCount} className="bdr-empty-cell">
//                       <div className="bdr-empty-state">
//                         <i
//                           className="ti ti-file-off bdr-empty-icon"
//                           aria-hidden="true"
//                         ></i>
//                         <h5 className="bdr-empty-title">No Drug Reports Found</h5>
//                         <p className="bdr-empty-desc">
//                           Bulk drug reports you add will appear here, organized
//                           and ready to review.
//                         </p>
//                         <button
//                           type="button"
//                           className="bdr-btn bdr-btn-primary"
//                           onClick={handleAddReport}
//                         >
//                           <i className="ti ti-plus"></i>
//                           <span className="bdr-btn-label">Add Report</span>
//                         </button>
//                       </div>
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>
//       </div>

//       <Footer />
//     </div>
//   );
// };

// export default BulkDrugReport;
